import type { FC } from 'react';

type LogoVariant = 'icon' | 'horizontal' | 'vertical';

type LogoProps = {
  className?: string;
  variant?: LogoVariant;
  imgSrc?: string;
};

export const Logo: FC<LogoProps> = ({ className = '', variant = 'horizontal', imgSrc }) => {
  if (imgSrc) {
    return (
      <img
        src={imgSrc}
        alt="AarogyaFlow Logo"
        className={`object-contain ${variant === 'icon' ? 'h-10 w-10' : variant === 'vertical' ? 'h-24 w-auto' : 'h-10 w-auto'} ${className}`}
      />
    );
  }

  const Icon = () => (
    <svg viewBox="0 0 375 375" fill="none" xmlns="http://www.w3.org/2000/svg" className="h-full w-auto overflow-visible">
      <defs>
        <clipPath id="logo-clip">
          <path d="M 45.800781 11.429688 L 329.164062 11.429688 L 329.164062 337.390625 L 45.800781 337.390625 Z M 45.800781 11.429688" clipRule="nonzero" />
        </clipPath>
      </defs>

      <path
        className="fill-slate-900 transition-colors dark:fill-white"
        fillOpacity="1"
        fillRule="nonzero"
        d="M 230.699219 175.9375 L 195.570312 88.488281 L 171.371094 28.257812 C 170.800781 26.835938 168.972656 26.507812 167.804688 27.257812 L 167.796875 26.867188 L 167.566406 27.429688 C 167.316406 27.644531 167.105469 27.921875 166.972656 28.257812 L 142.773438 88.488281 L 142.875 88.738281 L 68.570312 273.203125 C 78.394531 263.023438 90.554688 253.648438 104.914062 245.222656 C 115.121094 239.238281 126.648438 233.613281 139.363281 228.40625 L 169.246094 154.382812 L 190.660156 207.691406 C 230.128906 192.421875 278.175781 166.191406 293.679688 115.757812 C 294.457031 113.230469 295.132812 110.785156 295.714844 108.382812 C 282.167969 141.660156 249.609375 164.789062 230.699219 175.9375"
      />

      <g clipPath="url(#logo-clip)">
        <path
          className="fill-[#188ec4]"
          fillOpacity="1"
          fillRule="nonzero"
          d="M 329.085938 116.796875 C 327.898438 146.277344 312.929688 175.464844 289.125 195.980469 C 277.320312 206.152344 263.882812 215.023438 249.507812 222.800781 C 244.207031 225.667969 238.78125 228.386719 233.257812 230.964844 C 233.207031 230.992188 233.152344 231.015625 233.101562 231.039062 C 223.871094 235.351562 214.386719 239.273438 204.792969 242.859375 C 191.671875 247.769531 178.363281 252.058594 165.269531 255.835938 C 133.0625 265.128906 98.363281 282.292969 71.546875 304.15625 C 98.140625 279.988281 134.199219 260.601562 167.757812 249.988281 C 211.335938 236.207031 257.214844 216.699219 289.75 187.070312 C 320.316406 159.234375 335.066406 116.679688 323.179688 78.941406 C 311.296875 41.207031 271.128906 11.152344 227.75 11.429688 C 227.75 11.429688 268.199219 20.15625 285.957031 51.578125 C 301.796875 79.609375 303.1875 96.5 296.738281 119.960938 C 279.335938 183.273438 210.28125 210.269531 169.886719 224.574219 C 129.488281 238.875 88.71875 260.808594 65.636719 294.035156 C 54.519531 310.035156 47.613281 332.503906 45.800781 337.390625 C 72.628906 303.410156 112.121094 281.667969 165.269531 263.359375 C 178.96875 258.640625 193.449219 254.722656 207.417969 249.398438 L 237.328125 323.855469 L 290.121094 323.855469 L 252 228.960938 C 265.4375 221.511719 278.007812 213.089844 289.125 203.503906 C 314.953125 181.246094 330.382812 148.773438 329.085938 116.796875"
        />
      </g>
    </svg>
  );

  if (variant === 'icon') {
    return (
      <div className={`relative flex items-center justify-center ${className}`}>
        <Icon />
      </div>
    );
  }

  if (variant === 'vertical') {
    return (
      <div className={`flex flex-col items-center gap-4 ${className}`}>
        <div className="h-28 w-28">
          <Icon />
        </div>
        <div className="text-center">
          <div className="flex items-center justify-center text-3xl font-bold tracking-tight">
            <span className="text-slate-900 transition-colors dark:text-white">Aarogya</span>
            <span className="text-[#188ec4]">Flow</span>
          </div>
          <p className="mt-1 text-sm font-medium tracking-wide text-[#188ec4]">Simplifying Doctor Practices</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`flex items-center gap-3 ${className}`}>
      <div className="h-10 w-10">
        <Icon />
      </div>
      <div className="flex flex-col justify-center">
        <div className="flex items-center text-lg font-bold leading-none tracking-tight sm:text-xl">
          <span className="text-slate-900 transition-colors dark:text-white">Aarogya</span>
          <span className="text-[#188ec4]">Flow</span>
        </div>
        <span className="hidden pt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500 dark:text-slate-400 sm:inline">
          Simplifying Doctor Practices
        </span>
      </div>
    </div>
  );
};
